/**
 * Autopilot Dashboard — live view per session.
 * Polls every 30s. Shows status toggle + decision feed + alerts inbox + Pro chat trigger.
 */
import { useState, type ReactNode } from "react";
import { useParams, useLocation, Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Activity,
  Pause,
  Coffee,
  Square,
  AlertTriangle,
  MessageSquare,
  Download,
  ShieldCheck,
  ShieldAlert,
  Workflow,
  Trash2,
  RefreshCw,
  ImageDown,
  Wand2,
  Target,
  X as XIcon,
  TrendingUp,
  FlaskConical,
  Sparkles,
  AlertCircle,
  CalendarClock,
} from "lucide-react";
import {
  useAutopilotDashboard,
  useSetSessionStatus,
  useResolveAlert,
  useStrategistChat,
  useAutopilotPreflight,
  useDeleteAutopilotSession,
  useBackfillSnapshots,
  useImportMetaAds,
  useGenerateBriefs,
  useGenerateStrategy,
  useAutopilotPendingDecisions,
  useApplyStrategyDecision,
  useDismissStrategyDecision,
  type StrategyPlan,
  type PendingStrategyDecision,
} from "@/hooks/useAutopilot";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { FriendlyErrorMapper } from "@/lib/errors/FriendlyErrorMapper";
import { PermissionsMatrix } from "@/components/autopilot/PermissionsMatrix";
import { VolumeMeter } from "@/components/autopilot/VolumeMeter";
import { DiagnosticPanel } from "@/components/autopilot/DiagnosticPanel";

export default function AutopilotDashboard() {
  const params = useParams<{ id: string }>();
  const sessionId = params.id;
  const { data, isLoading } = useAutopilotDashboard(sessionId);
  const { data: preflight } = useAutopilotPreflight(sessionId);
  const { data: pendingDecisionsData } = useAutopilotPendingDecisions(sessionId);
  const applyDecision = useApplyStrategyDecision(sessionId);
  const dismissDecision = useDismissStrategyDecision(sessionId);
  const pendingDecisions = pendingDecisionsData?.items ?? [];
  const setStatus = useSetSessionStatus(sessionId);
  const resolveAlert = useResolveAlert(sessionId);
  const chat = useStrategistChat();
  const deleteSession = useDeleteAutopilotSession();
  const backfill = useBackfillSnapshots(sessionId ?? "");
  const importMeta = useImportMetaAds(sessionId ?? "");
  const generateBriefs = useGenerateBriefs(sessionId ?? "");
  const generateStrategy = useGenerateStrategy(sessionId ?? "");
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [chatOutput, setChatOutput] = useState<string | null>(null);

  const handleGenerateBriefs = async () => {
    if (!sessionId) return;
    try {
      const result = await generateBriefs.mutateAsync();
      if (result.error) {
        toast({
          title: "Generowanie briefów nie udało się",
          description: result.error,
          variant: "destructive",
        });
        return;
      }
      toast({
        title:
          result.generated > 0
            ? `Wygenerowano ${result.generated} briefów`
            : "Nie wygenerowano nowych briefów",
        description:
          result.generated > 0
            ? `Sprawdź pipeline kreatywny — pomysły są w kolumnie "Pomysły". Akceptuj klikając kartę.`
            : `Już masz wystarczająco briefów na ten tydzień (skipped: ${result.skipped}). Włączą się więcej gdy poprzednie się przeładują.`,
      });
    } catch (err) {
      toast({
        title: "Błąd generowania",
        description: FriendlyErrorMapper.map(err).description,
        variant: "destructive",
      });
    }
  };

  const handleImportMeta = async () => {
    if (!sessionId) return;
    try {
      const result = await importMeta.mutateAsync(undefined);
      if (result.error) {
        toast({
          title: "Import nie udał się",
          description:
            result.error === "no_meta_ad_account"
              ? "Sesja nie ma podpiętego konta Meta."
              : result.error === "no_access_token"
                ? "Brak tokena Meta — połącz integrację Meta."
                : result.error === "no_ads_found"
                  ? "Konto reklamowe nie ma adsów z impressions w ostatnich 7 dniach."
                  : result.error,
          variant: "destructive",
          action:
            result.error === "no_access_token" ? (
              <ToastAction
                altText="Połącz Metę"
                onClick={() => setLocation("/integracje?open=meta")}
              >
                Połącz Metę
              </ToastAction>
            ) : undefined,
        });
        return;
      }
      toast({
        title:
          result.imported > 0
            ? `Zaimportowano ${result.imported} reklam`
            : "Wszystkie adsy już zaimportowane",
        description:
          result.skipped > 0
            ? `Pominięto ${result.skipped} (już w pipeline).`
            : 'Karty wpadły do "W trakcie testu" w pipeline kreatywnym.',
      });
    } catch (err) {
      toast({
        title: "Błąd importu",
        description: FriendlyErrorMapper.map(err).description,
        variant: "destructive",
      });
    }
  };

  const handleBackfill = async () => {
    if (!sessionId) return;
    try {
      const result = await backfill.mutateAsync();
      if (result.error) {
        toast({
          title: "Backfill nie udał się",
          description:
            result.error === "no_meta_ad_account"
              ? "Sesja nie ma podpiętego konta Meta — wróć do wizarda i wybierz konto."
              : result.error === "no_access_token"
                ? "Brak tokena Meta dla użytkownika — połącz integrację Meta."
                : result.error,
          variant: "destructive",
          action:
            result.error === "no_access_token" ? (
              <ToastAction
                altText="Połącz Metę"
                onClick={() => setLocation("/integracje?open=meta")}
              >
                Połącz Metę
              </ToastAction>
            ) : undefined,
        });
        return;
      }
      toast({
        title: result.snapshotsInserted > 0 ? "Pobrano dane z Meta" : "Brak danych z Meta",
        description:
          result.snapshotsInserted > 0
            ? `${result.snapshotsInserted} snapshotów dodanych (ostatnie 7 dni). Diagnoza za chwilę się przeliczy.`
            : "Meta API zwróciło 0 wierszy — sprawdź czy konto reklamowe ma kampanie z impressions w ostatnich 7 dniach.",
      });
    } catch (err) {
      toast({
        title: "Nie udało się pobrać danych",
        description: FriendlyErrorMapper.map(err).description,
        variant: "destructive",
      });
    }
  };

  const handleDelete = () => {
    if (!sessionId) return;
    if (
      !confirm(
        "Usunąć tę sesję autopilota? Tej operacji nie da się cofnąć — kasują się wszystkie decyzje, alerty i snapshoty KPI."
      )
    )
      return;
    deleteSession.mutate(sessionId, {
      onSuccess: () => {
        toast({ title: "Sesja usunięta" });
        setLocation("/admin/autopilot");
      },
      onError: (err) =>
        toast({
          title: "Nie udało się usunąć sesji",
          description: FriendlyErrorMapper.map(err).description,
          variant: "destructive",
        }),
    });
  };

  if (isLoading || !data) {
    return <div className="p-6 text-muted-foreground">Ładowanie dashboardu…</div>;
  }

  const { session, recentDecisions, pendingAlerts, recentSnapshots, creativeStatus } = data;
  const isActive = session.status === "active";

  const askChat = async (intent: "plan_this_week" | "diagnose" | "retrospective") => {
    setChatOutput("Generowanie…");
    const r = await chat.mutateAsync({ sessionId, intent });
    setChatOutput(r.text);
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="container mx-auto p-6 max-w-6xl space-y-6">
        <a
          href="/admin/autopilot"
          className="text-sm text-white/60 hover:text-white inline-flex items-center gap-1"
        >
          ← Autopilot Ads
        </a>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{session.industry ?? "Sesja autopilota"}</h1>
            <p className="text-sm text-muted-foreground">
              {session.funnelType} · {session.market} · tryb:{" "}
              <Badge variant="secondary">{session.mode}</Badge>
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link href="/admin/autopilot/pipeline">
              <Button variant="outline" size="sm" className="gap-2">
                <Workflow className="w-4 h-4" /> Pipeline kreatywny
              </Button>
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={handleBackfill}
              disabled={backfill.isPending}
              title="Ściągnij ostatnie 7 dni z Meta API do snapshotów"
              className="gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${backfill.isPending ? "animate-spin" : ""}`} />
              {backfill.isPending ? "Pobieranie…" : "Pobierz dane z Meta"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleImportMeta}
              disabled={importMeta.isPending}
              title="Zaimportuj top adsów z konta Meta jako baseline w pipeline kreatywnym"
              className="gap-2"
            >
              <ImageDown className={`w-4 h-4 ${importMeta.isPending ? "animate-pulse" : ""}`} />
              {importMeta.isPending ? "Importuję…" : "Importuj adsy z Meta"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleGenerateBriefs}
              disabled={generateBriefs.isPending}
              title="AI (Claude) wygeneruje briefy reklamowe i wpisze je do pipeline (kolumna Pomysły)"
              className="gap-2"
            >
              <Wand2 className={`w-4 h-4 ${generateBriefs.isPending ? "animate-pulse" : ""}`} />
              {generateBriefs.isPending ? "Generuję…" : "Generuj nowe pomysły AI"}
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <a
                href={`/api/autopilot/sessions/${sessionId}/audit-log.csv`}
                download
                className="gap-2 inline-flex items-center"
              >
                <Download className="w-4 h-4" /> CSV
              </a>
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <a
                href={`/api/autopilot/sessions/${sessionId}/audit-log.json`}
                download
                className="gap-2 inline-flex items-center"
              >
                <Download className="w-4 h-4" /> JSON
              </a>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDelete}
              disabled={deleteSession.isPending}
              className="gap-2 text-red-400 border-red-500/30 hover:bg-red-500/10 hover:text-red-300"
            >
              <Trash2 className="w-4 h-4" /> Usuń
            </Button>
            {isActive ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setStatus.mutate("pause")}
                  className="gap-2"
                >
                  <Pause className="w-4 h-4" /> Pauza
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setStatus.mutate("vacation")}
                  className="gap-2"
                >
                  <Coffee className="w-4 h-4" /> Vacation Mode
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setStatus.mutate("stop")}
                  className="gap-2"
                >
                  <Square className="w-4 h-4" /> Stop
                </Button>
              </>
            ) : (
              <Button
                onClick={() => setStatus.mutate("activate")}
                disabled={preflight && !preflight.ready}
                className="gap-2"
                title={
                  preflight && !preflight.ready
                    ? "Preflight checks failed — resolve below first"
                    : undefined
                }
              >
                {preflight && !preflight.ready ? (
                  <ShieldAlert className="w-4 h-4" />
                ) : (
                  <ShieldCheck className="w-4 h-4" />
                )}
                Aktywuj
              </Button>
            )}
          </div>
        </div>

        {preflight && !preflight.ready && (
          <Card className="border-red-500 border-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-500">
                <ShieldAlert className="w-5 h-5" />
                Preflight: {preflight.summary.failed} required check(s) failed
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {preflight.checks
                .filter((c) => c.status === "fail")
                .map((c) => (
                  <div key={c.id} className="border-b pb-2">
                    <div className="font-medium flex items-center gap-2">
                      <Badge variant={c.severity === "required" ? "destructive" : "outline"}>
                        {c.severity}
                      </Badge>
                      {c.label}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">{c.details}</div>
                    {c.remediation && <div className="text-xs italic mt-1">→ {c.remediation}</div>}
                  </div>
                ))}
              {preflight.checks.filter((c) => c.status === "warning").length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground">
                    + {preflight.checks.filter((c) => c.status === "warning").length} warning(s) —
                    recommended fixes
                  </summary>
                  <div className="mt-2 space-y-1">
                    {preflight.checks
                      .filter((c) => c.status === "warning")
                      .map((c) => (
                        <div key={c.id} className="pl-4 border-l-2 border-yellow-500">
                          <span className="font-medium">{c.label}:</span> {c.details}
                          {c.remediation && <span className="block italic">→ {c.remediation}</span>}
                        </div>
                      ))}
                  </div>
                </details>
              )}
            </CardContent>
          </Card>
        )}

        {pendingAlerts.length > 0 && (
          <Card className="border-yellow-500 border-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                Inbox akcji ({pendingAlerts.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {pendingAlerts.map((a) => (
                <div key={a.id} className="flex items-center justify-between border-b pb-2">
                  <div>
                    <div className="font-medium">{a.type}</div>
                    <div className="text-xs text-muted-foreground">
                      {a.severity} ·{" "}
                      {a.createdAt ? new Date(a.createdAt).toLocaleString("pl-PL") : ""}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => resolveAlert.mutate({ alertId: a.id, decision: "approve" })}
                    >
                      Zatwierdź
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => resolveAlert.mutate({ alertId: a.id, decision: "dismiss" })}
                    >
                      Odrzuć
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Strategy plan — generowany przez Claude po aktywacji. Pokazuje 30-day plan akcyjny. */}
        {(() => {
          const cfg = (session.config ?? {}) as Record<string, unknown>;
          const strategy = cfg.initialStrategy as StrategyPlan | undefined;
          const accountStateLabel: Record<string, string> = {
            empty: "Konto puste — brak danych",
            data_starved: "Mało danych (faza startowa)",
            has_data: "Są pierwsze konwersje",
            has_winners: "Są winners do skalowania",
            fatigued: "Winners się męczą",
          };
          return (
            <Card className="border-purple-500/40">
              <CardHeader>
                <CardTitle className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-purple-400" /> Plan strategiczny — 30 dni
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      const r = await generateStrategy.mutateAsync();
                      if (r.ok) toast({ title: "Plan wygenerowany" });
                      else
                        toast({
                          title: "Nie udało się",
                          description: r.error,
                          variant: "destructive",
                        });
                    }}
                    disabled={generateStrategy.isPending}
                    className="gap-2"
                  >
                    <RefreshCw
                      className={`w-3.5 h-3.5 ${generateStrategy.isPending ? "animate-spin" : ""}`}
                    />
                    {strategy ? "Wygeneruj ponownie" : "Wygeneruj plan"}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!strategy ? (
                  <p className="text-sm text-muted-foreground">
                    Brak planu. Claude analizuje twoje konto Meta, KPI cele i firm-assets — buduje
                    konkretny 30-dniowy plan akcji (co wyłączyć, skalować, testować, jakie kreacje
                    produkować). Generuje się automatycznie przy aktywacji sesji albo kliknij
                    "Wygeneruj plan".
                  </p>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="secondary" className="text-xs">
                        {accountStateLabel[strategy.accountState] ?? strategy.accountState}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground">
                        wygenerowane {new Date(strategy.generatedAt).toLocaleString("pl-PL")}
                      </span>
                    </div>
                    <p className="text-sm leading-relaxed">{strategy.summary}</p>

                    {strategy.whatToKill.length > 0 && (
                      <ActionableStrategySection
                        icon={<XIcon className="h-4 w-4 text-red-400" />}
                        title="❌ Co wyłączyć"
                        items={strategy.whatToKill.map((k) => ({
                          primary: k.what,
                          secondary: k.why,
                          targetId: k.targetId,
                          targetName: k.targetName,
                          actionLabel: "Wyłącz w Meta",
                        }))}
                        pendingDecisions={pendingDecisions}
                        actionFilter={(d) => d.action === "kill"}
                        onApply={(id) => applyDecision.mutateAsync(id)}
                        onDismiss={(id) => dismissDecision.mutateAsync({ decisionId: id })}
                        isApplying={applyDecision.isPending}
                        isDismissing={dismissDecision.isPending}
                      />
                    )}
                    {strategy.whatToScale.length > 0 && (
                      <ActionableStrategySection
                        icon={<TrendingUp className="h-4 w-4 text-emerald-400" />}
                        title="📈 Co skalować"
                        items={strategy.whatToScale.map((s) => ({
                          primary: s.what,
                          secondary: `${s.why} (impact: ${s.estimatedImpact})`,
                          targetId: s.targetId,
                          targetName: s.targetName,
                          actionLabel: `Skaluj +${s.budgetIncreasePct ?? 20}%`,
                        }))}
                        pendingDecisions={pendingDecisions}
                        actionFilter={(d) => d.action.startsWith("scale_up_")}
                        onApply={(id) => applyDecision.mutateAsync(id)}
                        onDismiss={(id) => dismissDecision.mutateAsync({ decisionId: id })}
                        isApplying={applyDecision.isPending}
                        isDismissing={dismissDecision.isPending}
                      />
                    )}
                    {strategy.whatToTest.length > 0 && (
                      <StrategySection
                        icon={<FlaskConical className="h-4 w-4 text-amber-400" />}
                        title="🧪 Co testować"
                        items={strategy.whatToTest.map((t) => ({
                          primary: t.hypothesis,
                          secondary: `${t.reason} (metryka: ${t.metric})`,
                        }))}
                      />
                    )}
                    {strategy.whatToCreate.length > 0 && (
                      <StrategySection
                        icon={<Sparkles className="h-4 w-4 text-cyan-400" />}
                        title="🎬 Co produkować (kreacje)"
                        items={strategy.whatToCreate.map((c) => ({
                          primary: `${c.creativeType} — ${c.angle}`,
                          secondary: c.reason,
                        }))}
                      />
                    )}
                    {strategy.missingPieces.length > 0 && (
                      <StrategySection
                        icon={<AlertCircle className="h-4 w-4 text-orange-400" />}
                        title="⚠️ Brakuje (musisz dostarczyć)"
                        items={strategy.missingPieces.map((m) => ({
                          primary: m.item,
                          secondary: m.whyNeeded,
                        }))}
                        highlight
                      />
                    )}
                    {strategy.weeklyMilestones.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 text-sm font-semibold mb-2">
                          <CalendarClock className="h-4 w-4 text-blue-400" /> Kamienie milowe
                          (tydzień po tygodniu)
                        </div>
                        <div className="grid gap-2 md:grid-cols-2">
                          {strategy.weeklyMilestones.map((m) => (
                            <div
                              key={m.week}
                              className="border border-white/10 rounded p-2 text-xs"
                            >
                              <div className="font-semibold mb-1">Tydzień {m.week}</div>
                              <div className="text-muted-foreground">{m.goal}</div>
                              <div className="mt-1 text-[10px]">
                                <span className="text-muted-foreground">{m.keyMetric}:</span>{" "}
                                <strong>{m.targetValue}</strong>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {creativeStatus && (
          <Card className="border-cyan-500/40">
            <CardHeader>
              <CardTitle className="flex items-center justify-between gap-2 flex-wrap">
                <span className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-cyan-400" /> Wygenerowane kreacje
                </span>
                <Link href="/autopilot/pipeline">
                  <Button size="sm" variant="outline" className="gap-2">
                    <Workflow className="w-3.5 h-3.5" /> Otwórz pipeline
                  </Button>
                </Link>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <div className="border rounded p-2">
                  <div className="text-muted-foreground">Briefy (czekają)</div>
                  <div className="text-lg font-semibold">{creativeStatus.pendingBriefsCount}</div>
                  {creativeStatus.failedBriefsCount > 0 && (
                    <div className="text-[10px] text-red-400 mt-0.5">
                      {creativeStatus.failedBriefsCount} błąd generacji
                    </div>
                  )}
                </div>
                <div className="border rounded p-2">
                  <div className="text-muted-foreground">Wariacje (do akceptacji)</div>
                  <div className="text-lg font-semibold">
                    {creativeStatus.awaitingApprovalCount}
                  </div>
                </div>
                <div className="border rounded p-2">
                  <div className="text-muted-foreground">Gotowe do Meta</div>
                  <div className="text-lg font-semibold">{creativeStatus.readyToUploadCount}</div>
                </div>
                <div className="border rounded p-2">
                  <div className="text-muted-foreground">Łącznie z mediami</div>
                  <div className="text-lg font-semibold">
                    {creativeStatus.recentVariants.length}+
                  </div>
                </div>
              </div>
              {creativeStatus.failedBriefsCount > 0 && (
                <div className="text-xs text-red-400 bg-red-950/30 border border-red-900/40 rounded px-2 py-1.5">
                  ⚠️ {creativeStatus.failedBriefsCount} brief(ów) nie wyrenderowało się. Wejdź w
                  pipeline i kliknij "Wygeneruj wizualizację" żeby spróbować ponownie.
                </div>
              )}
              {creativeStatus.recentVariants.length > 0 ? (
                <div>
                  <div className="text-xs text-muted-foreground mb-1.5">
                    Ostatnie wygenerowane (4):
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {creativeStatus.recentVariants.map((v) => (
                      <Link key={v.id} href="/autopilot/pipeline">
                        <div className="border rounded overflow-hidden hover:border-cyan-400 cursor-pointer">
                          {v.thumbnailUrl ? (
                            <img
                              src={v.thumbnailUrl}
                              alt={v.title}
                              className="w-full h-24 object-cover"
                            />
                          ) : (
                            <div className="w-full h-24 bg-muted flex items-center justify-center text-xs text-muted-foreground">
                              {v.mediaType === "video" ? "🎬" : "🖼️"}
                            </div>
                          )}
                          <div className="p-1 text-[10px] truncate" title={v.title}>
                            {v.title}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">
                  Brak wygenerowanych kreacji. Autopilot je produkuje gdy są briefy w pipeline —
                  kliknij "Wygeneruj briefy" w pasku statusu lub poczekaj na cykl dzienny.
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Live decision feed (ostatnie 50)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 max-h-96 overflow-auto">
              {recentDecisions.length === 0 && (
                <div className="text-sm text-muted-foreground">Brak decyzji.</div>
              )}
              {recentDecisions.map((d) => (
                <div key={d.id} className="text-xs flex gap-2 items-start py-1 border-b">
                  <Badge variant={d.applied ? "default" : "outline"} className="shrink-0">
                    {d.applied ? "✓" : "·"}
                  </Badge>
                  <div className="flex-1">
                    <span className="font-mono">{d.action}</span>
                    <span className="text-muted-foreground">
                      {" "}
                      ({d.targetType}:{(d.targetId ?? "").slice(0, 12)})
                    </span>
                    <div className="text-muted-foreground italic mt-0.5">
                      {(d.reasoning ?? "").slice(0, 200)}
                    </div>
                  </div>
                  <div className="text-muted-foreground shrink-0">
                    {d.decidedAt ? new Date(d.decidedAt).toLocaleTimeString("pl-PL") : ""}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4" /> Pro Mode chat
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                size="sm"
                variant="outline"
                className="w-full justify-start"
                onClick={() => askChat("plan_this_week")}
              >
                📅 Plan tygodnia
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="w-full justify-start"
                onClick={() => askChat("diagnose")}
              >
                🔍 Diagnose account
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="w-full justify-start"
                onClick={() => askChat("retrospective")}
              >
                📊 Retro 30d
              </Button>
              {chatOutput && (
                <div className="mt-3 text-xs whitespace-pre-wrap bg-muted/30 p-3 rounded max-h-64 overflow-auto">
                  {chatOutput}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <DiagnosticPanel sessionId={sessionId} />

        <VolumeMeter sessionId={sessionId} />

        <PermissionsMatrix sessionId={sessionId} />

        <Card>
          <CardHeader>
            <CardTitle>KPI snapshots ({recentSnapshots.length})</CardTitle>
          </CardHeader>
          <CardContent className="text-xs space-y-1 max-h-64 overflow-auto">
            {recentSnapshots.slice(0, 30).map((s) => (
              <div key={s.id} className="font-mono">
                {s.capturedAt ? new Date(s.capturedAt).toISOString().slice(0, 16) : ""} ·{" "}
                {s.targetType}:{(s.targetId ?? "").slice(0, 10)} · CPA={s.cpa ?? "—"} · ROAS=
                {s.roas ?? "—"} · CPM={s.cpm ?? "—"} · Conv={s.conversions ?? 0}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

interface StrategySectionProps {
  icon: ReactNode;
  title: string;
  items: Array<{ primary: string; secondary: string }>;
  highlight?: boolean;
}

function StrategySection({ icon, title, items, highlight = false }: StrategySectionProps) {
  if (items.length === 0) return null;
  return (
    <div
      className={`rounded-md border p-3 ${
        highlight ? "border-orange-500/40 bg-orange-500/5" : "border-zinc-800 bg-zinc-900/30"
      }`}
    >
      <div className="flex items-center gap-2 text-sm font-semibold mb-2">
        {icon}
        <span>{title}</span>
        <span className="text-xs text-muted-foreground font-normal">({items.length})</span>
      </div>
      <ul className="space-y-1.5">
        {items.map((item, idx) => (
          <li key={idx} className="text-xs">
            <div className="font-medium text-zinc-100">{item.primary}</div>
            <div className="text-muted-foreground mt-0.5">{item.secondary}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface ActionableItem {
  primary: string;
  secondary: string;
  targetId?: string;
  targetName?: string;
  actionLabel: string;
}

interface ActionableStrategySectionProps {
  icon: ReactNode;
  title: string;
  items: ActionableItem[];
  pendingDecisions: PendingStrategyDecision[];
  actionFilter: (d: PendingStrategyDecision) => boolean;
  onApply: (decisionId: string) => Promise<unknown>;
  onDismiss: (decisionId: string) => Promise<unknown>;
  isApplying: boolean;
  isDismissing: boolean;
}

function ActionableStrategySection({
  icon,
  title,
  items,
  pendingDecisions,
  actionFilter,
  onApply,
  onDismiss,
  isApplying,
  isDismissing,
}: ActionableStrategySectionProps) {
  if (items.length === 0) return null;
  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-900/30 p-3">
      <div className="flex items-center gap-2 text-sm font-semibold mb-2">
        {icon}
        <span>{title}</span>
        <span className="text-xs text-muted-foreground font-normal">({items.length})</span>
      </div>
      <ul className="space-y-2">
        {items.map((item, idx) => {
          const decision = item.targetId
            ? pendingDecisions.find((d) => d.targetId === item.targetId && actionFilter(d))
            : undefined;
          const errorMsg =
            decision?.metricsSnapshot &&
            typeof (decision.metricsSnapshot as Record<string, unknown>).error === "string"
              ? ((decision.metricsSnapshot as Record<string, unknown>).error as string)
              : null;
          return (
            <li key={idx} className="text-xs border-l-2 border-zinc-800 pl-2">
              <div className="font-medium text-zinc-100">{item.primary}</div>
              <div className="text-muted-foreground mt-0.5">{item.secondary}</div>
              {item.targetName && (
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  Cel: <code>{item.targetName}</code>
                </div>
              )}
              {errorMsg && (
                <div className="text-[10px] text-red-300 mt-1">Poprzednia próba: {errorMsg}</div>
              )}
              {decision && (
                <div className="flex gap-2 mt-1.5">
                  <Button
                    size="sm"
                    variant="default"
                    className="h-7 text-[11px]"
                    disabled={isApplying}
                    onClick={() => onApply(decision.id)}
                  >
                    {item.actionLabel}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-[11px]"
                    disabled={isDismissing}
                    onClick={() => onDismiss(decision.id)}
                  >
                    Zignoruj
                  </Button>
                </div>
              )}
              {!decision && item.targetId && (
                <div className="text-[10px] text-muted-foreground mt-1">
                  (Brak pending decision dla tego ID — wygeneruj plan ponownie)
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
